import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';

import CropEditor from 'react-avatar-editor';

import { ModalOpener } from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InputSlider from 'components/inputs/input-slider/InputSlider';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ImageUploader, { MEDIA_TYPES_IMAGES } from 'components/ImageUploader';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';
import { validateImage } from 'utils/image';
import { getDefaultCenteredArea, hasArea } from 'utils/cloudinary';

applyTranslations(require('./image-cropper.i18n.json'));

const MAX_IMAGE_SIZE_PIXEL = 25000000;
const MIN_SLIDER_VALUE = 0.2;
const MAX_SLIDER_VALUE = 3;

class ImageCropper extends Component {
  static propTypes = {
    mode: PropTypes.oneOf(['inline', 'modal']),
    image: PropTypes.shape({
      url: PropTypes.string
    }),
    width: PropTypes.number,
    height: PropTypes.number,
    border: PropTypes.number,
    borderRadius: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    dispatch: PropTypes.func
  };

  constructor(props) {
    super(props);

    const { image } = this.props;

    this.state = this.getDefaultState(image);
  }

  getDefaultState(image) {
    const area = hasArea(image) ?
      image.area :
      getDefaultCenteredArea();

    return {
      image,
      showModal: true, // immediately open
      scale: area.scale,
      position: { x: area.x + area.width / 2, y: area.y + area.height / 2 },
      rotate: area.rotate || 0
    };
  }

  handlePositionChange = (position) => {
    this.setState({
      position
    });
  };

  handleImageChange = (file) => {
    const { dispatch } = this.props;
    const validationError = validateImage(file);

    if (validationError) {
      dispatch(showConfirmModal({
        message: validationError,
        confirmButtonLabel: __t('image_cropper.action_button_ok'),
        onConfirm: () => dispatch(hideConfirmModal())
      }));

      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      this.setState(this.getDefaultState({
        file,
        url: reader.result
      }));
    };

    // async call
    reader.readAsDataURL(file);
  };

  handleSave = () => {
    const { onChange, onCancel, width, height } = this.props;
    const { image, scale, imageToCrop, rotate } = this.state;

    if (!image) {
      onCancel();
      return;
    }

    const croppingArea = this.editor.getCroppingRect();



    onChange({ // image
      ...image,
      area: {
        ...{
          ...croppingArea,
          height: parseFloat(croppingArea.height.toFixed(5)),
          width: parseFloat(croppingArea.width.toFixed(5))
        },
        scale,
        rotate,
        originalWidth: imageToCrop.resource.width,
        originalHeight: imageToCrop.resource.height
      }
    }, { // custom size
      width,
      height
    });
  };

  handleScaleChange = (value) => {
    this.setState({
      scale: value
    });
  };

  handleRotateLeft = () => {
    this.setState(state => ({
      rotate: state.rotate - 90
    }));
  };

  handleRotateRight = () => {
    this.setState(state => ({
      rotate: state.rotate + 90
    }));
  };

  handleImageToCropLoaded = (imageState) => {
    this.setState({
      imageToCrop: imageState
    });
  };

  getSliderMinValue = () => {
    const { imageToCrop } = this.state;

    if (!imageToCrop?.resource?.height || !imageToCrop?.resource?.width) {
      return MIN_SLIDER_VALUE;
    }

    for (let scale = 4; scale <= 20; scale++) {
      const minImageDimension = Math.min(imageToCrop.resource.height, imageToCrop.resource.width);
      const imageActualDimension = minImageDimension * (20 / scale);
      const imageArea = imageActualDimension * imageActualDimension;

      if (imageArea <= MAX_IMAGE_SIZE_PIXEL) {
        return (scale / 20);
      }
    }

    return 1;
  };

  renderCropper() {
    const { width, height, border, borderRadius } = this.props;
    const { scale, position, image, rotate } = this.state;
    const hasImage = image && image.url;

    const cropperClasses = classnames({
      'image-cropper__cropper': true,
      'image-cropper__cropper--with-image': hasImage,
      'image-cropper__cropper--rounded': borderRadius
    });

    return (
      <div className={cropperClasses}>
        <CropEditor
          ref={(editor) => { this.editor = editor; }}
          image={image ? image.url : null}
          width={width}
          height={height}
          border={border}
          borderRadius={borderRadius}
          color={[98, 105, 106, 1]} // RGBA
          position={position}
          scale={scale}
          rotate={rotate}
          style={{ display: 'block' }}
          onPositionChange={this.handlePositionChange}
          onLoadSuccess={this.handleImageToCropLoaded}
        />
        {
          hasImage &&
            <div className="image-cropper__cropper-reposition">
              <SVGDynamicIcon
                name="reposition-custom-fg"
                width={16}
                height={16}
                customColor="white"
              />
              <span className="image-cropper__cropper-reposition-label">
                {__t('image_cropper.cropped_image_reposition')}
              </span>
            </div>
        }
      </div>
    );
  }

  renderToolbar({ isNarrow, canRotate }) {
    const { image, imageToCrop } = this.state;

    const toolbarClasses = classnames({
      'image-cropper__toolbar': true,
      'image-cropper__toolbar--narrow': isNarrow
    });

    return (
      <div className={toolbarClasses}>
        <div className="image-cropper__toolbar-item image-cropper__toolbar-item--left">
          <ImageUploader
            button={{
              width: 'minimum',
              label: __t('image_cropper.action_button_upload')
            }}
            onUpload={this.handleImageChange}
            mediaTypes={MEDIA_TYPES_IMAGES}
          />
        </div>
        <div className="image-cropper__toolbar-item image-cropper__toolbar-item--center">
          {
            image && image.url &&
              <div>
                <InputSlider
                  minValue={this.getSliderMinValue(image)}
                  maxValue={MAX_SLIDER_VALUE}
                  step={0.05}
                  buttonStep={0.1}
                  showButtons
                  value={this.state.scale}
                  onChange={this.handleScaleChange}
                />
                {
                  canRotate &&
                    <span className="image-cropper__toolbar-rotates">
                      <span className="image-cropper__toolbar-rotate">
                        <SVGDynamicIcon
                          name="rotate-left-custom-fg"
                          width={18}
                          height={18}
                          customColor="black"
                          clickable
                          onClick={this.handleRotateLeft}
                        />
                      </span>
                      <span className="image-cropper__toolbar-rotate">
                        <SVGDynamicIcon
                          name="rotate-right-custom-fg"
                          width={18}
                          height={18}
                          customColor="black"
                          clickable
                          onClick={this.handleRotateRight}
                        />
                      </span>
                    </span>
                }
              </div>
          }
        </div>
        <div className="image-cropper__toolbar-item image-cropper__toolbar-item--right">
          <ActionButton
            color="tertiary"
            width="minimum"
            onClick={this.props.onCancel}
          >
            {__t('image_cropper.action_button_cancel')}
          </ActionButton>
          <ActionButton
            width="minimum"
            onClick={this.handleSave}
            disabled={!imageToCrop}
          >
            {__t('image_cropper.action_button_done')}
          </ActionButton>
        </div>
      </div>
    );
  }

  renderContent(options) {
    return (
      <section className="image-cropper">
        {this.renderCropper()}
        {this.renderToolbar(options)}
      </section>
    );
  }

  renderModal() {
    const { showModal } = this.state;

    return (
      <ModalOpener
        open={showModal}
        showClose={false}
        width="small"
      >
        {this.renderContent({ isNarrow: true, canRotate: true })}
      </ModalOpener>
    );
  }

  render() {
    const { mode } = this.props;

    if (mode === 'modal') {
      return this.renderModal();
    }

    return this.renderContent({ isNarrow: false, canRotate: false });
  }
}

export default connect()(ImageCropper);
